import { supabase } from './supabase';
import { PostgrestError } from '@supabase/supabase-js';

export interface ListingLike {
  id: string;
  user_id: string;
  listing_id: string;
  created_at: string;
}

export async function likeListing(listingId: string): Promise<{ error: PostgrestError | null }> {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error('Not authenticated');
  }

  // First add to listing_likes table
  const { error: likesError } = await supabase
    .from('listing_likes')
    .insert({ 
      listing_id: listingId,
      user_id: user.id
    });

  if (likesError) {
    return { error: likesError };
  }

  // Also add to saved_listings table for favorites page
  const { error: savedListingsError } = await supabase
    .from('saved_listings')
    .upsert({ 
      listing_id: listingId,
      user_id: user.id,
      created_at: new Date().toISOString()
    });

  return { error: savedListingsError };
}

export async function unlikeListing(listingId: string): Promise<{ error: PostgrestError | null }> {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error('Not authenticated');
  }

  // Remove from listing_likes table
  const { error: likesError } = await supabase
    .from('listing_likes')
    .delete()
    .eq('listing_id', listingId)
    .eq('user_id', user.id);

  if (likesError) {
    return { error: likesError };
  }

  // Also remove from saved_listings table for favorites page
  const { error: savedListingsError } = await supabase
    .from('saved_listings')
    .delete()
    .eq('listing_id', listingId)
    .eq('user_id', user.id);

  return { error: savedListingsError };
}

export async function isListingLiked(listingId: string): Promise<{ liked: boolean; error: PostgrestError | null }> {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user) {
    return { liked: false, error: userError };
  }

  const { data, error } = await supabase
    .from('listing_likes')
    .select('id')
    .eq('listing_id', listingId)
    .eq('user_id', user.id)
    .limit(1);

  return { liked: data && data.length > 0, error };
}

export async function getListingLikesCount(listingId: string): Promise<{ count: number; error: PostgrestError | null }> {
  const { count, error } = await supabase
    .from('listing_likes')
    .select('id', { count: 'exact', head: true })
    .eq('listing_id', listingId);

  return { count: count || 0, error };
}